/**
 * Expand a flat ``{"a.b.c": value}`` map into a nested
 * ``{a: {b: {c: value}}}`` object.
 *
 * Per-tenant i18n overrides are persisted with the i18next dotted key
 * path as the storage key (compact JSON, easy backend validation). At
 * merge time we have to convert that back into the nested shape
 * i18next stores internally — ``addResourceBundle`` does NOT split
 * dotted keys, so a flat ``{"cta.heading": "X"}`` would be stored
 * literally and never matched by a normal ``t("cta.heading")`` lookup
 * (which traverses ``data.cta.heading`` with the default keySeparator).
 */

export type FlatStrings = Record<string, string>;
export type NestedStrings = { [k: string]: NestedStrings | string };

const SEP = ".";

export function expandDottedKeys(flat: FlatStrings): NestedStrings {
  const out: NestedStrings = {};
  for (const [path, value] of Object.entries(flat)) {
    if (typeof value !== "string") continue;
    const parts = path.split(SEP);
    let cursor: NestedStrings = out;
    for (let i = 0; i < parts.length - 1; i++) {
      const key = parts[i];
      const next = cursor[key];
      if (typeof next !== "object" || next === null) {
        cursor[key] = {};
      }
      cursor = cursor[key] as NestedStrings;
    }
    cursor[parts[parts.length - 1]] = value;
  }
  return out;
}
